// ============================================================================
// GST EXPECTED FILE SERVICE (ADR-0012 §4, ADR-0025 §7, Revision 1)
// ============================================================================
// The files each GSTIN's client sends — per branch, each feeding streams — and
// whether each return period has them. Completeness is computed by the API on
// every read; nothing here caches it, because a superseded file changes it.

import { get, post, put, del } from '../client/axiosClient';
import { gstEndpoints } from '../endpoints/gst.endpoints';
import type {
  GstExpectedFile,
  GstPeriodCompleteness,
  GstPeriodCompletenessSummary,
  GstSuggestedPeriod,
  SaveGstExpectedFileRequest,
} from '../types/gstEngagement.types';

class GstExpectedSourceService {
  /** Every expected file, each with its streams. Older per-stream declarations are adopted into files on the way. */
  async listFiles(engagementId: string): Promise<GstExpectedFile[]> {
    const response = await get<any>(gstEndpoints.engagementExpectedFiles(engagementId));
    return (response.data?.data ?? []) as GstExpectedFile[];
  }

  async createFile(engagementId: string, request: SaveGstExpectedFileRequest): Promise<{ file: GstExpectedFile; message: string }> {
    const response = await post<any>(gstEndpoints.engagementExpectedFiles(engagementId), request);
    return { file: response.data?.data as GstExpectedFile, message: response.data?.message ?? 'Saved.' };
  }

  /** Name, mandatory flag and notes; GSTIN, branch and kind are fixed once created. */
  async updateFile(engagementId: string, fileId: string, request: SaveGstExpectedFileRequest): Promise<GstExpectedFile> {
    const response = await put<any>(gstEndpoints.engagementExpectedFileById(engagementId, fileId), request);
    return response.data?.data as GstExpectedFile;
  }

  async removeFile(engagementId: string, fileId: string): Promise<void> {
    await del<any>(gstEndpoints.engagementExpectedFileById(engagementId, fileId));
  }

  /** The earliest month in scope this file is not in for — the upload dialog's prefill. */
  async suggestPeriod(engagementId: string, fileId: string): Promise<GstSuggestedPeriod> {
    const response = await get<any>(gstEndpoints.engagementExpectedFileSuggestedPeriod(engagementId, fileId));
    return response.data?.data as GstSuggestedPeriod;
  }

  /** Mandatory files in / expected for every live return period. */
  async getSummaries(engagementId: string): Promise<GstPeriodCompletenessSummary[]> {
    const response = await get<any>(gstEndpoints.engagementSourceCompleteness(engagementId));
    return (response.data?.data ?? []) as GstPeriodCompletenessSummary[];
  }

  async getPeriod(engagementId: string, obligationId: string): Promise<GstPeriodCompleteness> {
    const response = await get<any>(gstEndpoints.engagementPeriodCompleteness(engagementId, obligationId));
    return response.data?.data as GstPeriodCompleteness;
  }

  /** Excuses a mandatory file for this return period; returns the period's new standing. */
  async waive(engagementId: string, obligationId: string, fileId: string, reason: string): Promise<GstPeriodCompleteness> {
    const response = await post<any>(gstEndpoints.engagementSourceWaivers(engagementId), { obligationId, fileId, reason });
    return response.data?.data as GstPeriodCompleteness;
  }

  async revokeWaiver(engagementId: string, obligationId: string, waiverId: string): Promise<GstPeriodCompleteness> {
    const response = await del<any>(gstEndpoints.engagementSourceWaiverById(engagementId, obligationId, waiverId));
    return response.data?.data as GstPeriodCompleteness;
  }
}

export const gstExpectedSourceService = new GstExpectedSourceService();
export default gstExpectedSourceService;
